import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useLatest } from 'react-use'
import { reject, reverse, sortBy, toNumber } from 'lodash-es'
import { Vector3 } from 'three'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Stack, Tab, Typography, styled } from '@mui/material'
import IFCFilterButton from '@editor/pages/IFCImport/components/IFCFilterButton'
import IFCMetadataPendingNotice from '@editor/pages/IFCImport/components/IFCMetadataPendingNotice'
import OpeningImportModeTabs from '@editor/pages/IFCImport/components/IFCOpeningImportPanel/OpeningImportModeTabs'
import IFCSelectionInfo from '@editor/pages/IFCImport/components/IFCSelectionInfo'
import IFCStoreyAssignmentSelect from '@editor/pages/IFCImport/components/IFCStoreyAssignmentSelect'
import IFCTypeAssignmentSelect from '@editor/pages/IFCImport/components/IFCTypeAssignmentSelect'
import { CorneredSection } from '@ui2/structure'
import { roofStoreyKey, useControlStore, useModelStore } from '@editorStores'
import { useIfcFiles, useSceneContextMenuAction, useUpdateIfcModelMetadata } from '@editorHooks'
import {
  IFCParseAsRoofSlabForm,
  IFCParseAsWallForm,
  IFCParseOptions,
  IfcPropertiesView,
} from './components'
import { IFCParseOption } from './components/ifcParseOptions'

interface Props {
  /** Selected IFC element GUIDs (single or multi-selection). */
  guids: string[]
  onClose: () => void
  /** When provided, allows "select by property" to set selection to all elements matching a property value. */
  onSelectGuids?: (guids: string[]) => void
}

type ParseMode = 'wall' | 'wallOpening' | 'roof'

/** Sub-tabs of the "Tools" section shown for a selected IFC element. */
type ToolsTab = 'filter' | 'type' | 'storey'

const StyledTabPanel = styled(TabPanel)({ padding: 0 })

/** Frontend shows GLB (converted from IFC). Parse task needs the raw IFC file id = parent of the GLB. */
const IFCElementForm = ({ guids, onClose, onSelectGuids }: Props): ReactElement => {
  const { projectId = '' } = useParams()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const glbFiles = useIfcFiles()
  const [parseMode, setParseMode] = useState<ParseMode>('wall')
  const [toolsTab, setToolsTab] = useState<ToolsTab>('filter')
  const activeGlbFile = activeIfcFileGuid
    ? glbFiles.find((f: FileItem) => f.guid === activeIfcFileGuid)
    : null
  const metadata = activeGlbFile?.additional_data?.model_metadata ?? null
  const ifcFileId = activeGlbFile?.parent_guid ?? null
  const { updateIfcModelMetadata } = useUpdateIfcModelMetadata(activeIfcFileGuid)
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const storeyOptions = useMemo(() => {
    const nonRoofStoreys = reverse(
      sortBy(
        reject(Array.from(availableStoreys), storey => storey === roofStoreyKey),
        storey => toNumber(storey),
      ),
    )

    return availableStoreys.has(roofStoreyKey) ? [roofStoreyKey, ...nonRoofStoreys] : nonRoofStoreys
  }, [availableStoreys])

  const updateIfcModelMetadataLatest = useLatest(updateIfcModelMetadata)

  const handleFilter = useCallback(
    (selectedElement: string | null) => {
      if (!selectedElement) return
      updateIfcModelMetadataLatest.current(current => {
        if (!current) return current
        return {
          ...current,
          filteredElements: [...(current.filteredElements ?? []), [selectedElement]],
        }
      })
    },
    [updateIfcModelMetadataLatest],
  )

  useSceneContextMenuAction({
    label: 'Filtern',
    action: handleFilter,
    available: (
      selectedElement: string | null,
      point: Vector3 | null,
      userDataActions: Record<string, unknown>,
    ) => {
      if (!selectedElement) return false

      if (!('filter' in userDataActions)) return false

      return true
    },
    group: 'default',
  })

  const parseModeForm = useMemo(() => {
    switch (parseMode) {
      case 'wallOpening':
        return (
          <OpeningImportModeTabs
            guids={guids}
            projectId={projectId}
            activeGlbFile={activeGlbFile ?? null}
            onSuccess={onClose}
          />
        )
      case 'roof':
        return (
          <IFCParseAsRoofSlabForm
            guids={guids}
            ifcFileId={ifcFileId}
            projectId={projectId}
            onSuccess={onClose}
          />
        )
      case 'wall':
      default:
        return (
          <IFCParseAsWallForm
            guids={guids}
            ifcFileId={ifcFileId}
            projectId={projectId}
            onSuccess={onClose}
          />
        )
    }
  }, [activeGlbFile, guids, ifcFileId, onClose, parseMode, projectId])

  // While the IFC metadata is still being read in the background job after upload, show only the
  // pending notice — none of the tools/parse forms below can operate without the metadata.
  if (!metadata)
    return (
      <Box sx={({ palette }) => ({ p: 2, borderBottom: `1px solid ${palette.divider}` })}>
        <IFCMetadataPendingNotice />
      </Box>
    )

  return (
    <Stack direction="column" spacing={1.5} p={1.5}>
      <CorneredSection title="Auswahl">
        <IFCSelectionInfo />
      </CorneredSection>

      <CorneredSection title="Tools" collapsible defaultOpen>
        <TabContext value={toolsTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList
              onChange={(_event, value: ToolsTab) => setToolsTab(value)}
              sx={{
                minHeight: 32,
                '& .MuiTab-root': { minHeight: 32, textTransform: 'none', py: 0.5 },
              }}
            >
              <Tab label="Filtern" value="filter" data-cy="tab-ifc-tools-filter" />
              <Tab label="Typ zuweisen" value="type" data-cy="tab-ifc-tools-type" />
              <Tab label="Stockwerk zuweisen" value="storey" data-cy="tab-ifc-tools-storey" />
            </TabList>
          </Box>

          <StyledTabPanel value="filter">
            <Box sx={{ pt: 1 }}>
              <IFCFilterButton
                guids={guids}
                disabled={!activeGlbFile}
                updateIfcModelMetadata={updateIfcModelMetadata}
                onFiltered={onClose}
                enableSpaceHotkey
              />
            </Box>
          </StyledTabPanel>

          <StyledTabPanel value="type">
            <Box sx={{ pt: 1 }}>
              <IFCTypeAssignmentSelect
                guids={guids}
                metadata={metadata}
                disabled={!activeGlbFile}
                updateIfcModelMetadata={updateIfcModelMetadata}
              />
            </Box>
          </StyledTabPanel>

          <StyledTabPanel value="storey">
            <Box sx={{ pt: 1 }}>
              <IFCStoreyAssignmentSelect
                guids={guids}
                metadata={metadata}
                storeyOptions={storeyOptions}
                disabled={!activeGlbFile}
                updateIfcModelMetadata={updateIfcModelMetadata}
              />
            </Box>
          </StyledTabPanel>
        </TabContext>
      </CorneredSection>

      <CorneredSection title="Element importieren" collapsible>
        <TabContext value={parseMode}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList
              onChange={(_event, value: ParseMode) => setParseMode(value)}
              sx={{
                minHeight: 32,
                '& .MuiTab-root': { minHeight: 32, textTransform: 'none', py: 0.5 },
              }}
            >
              {IFCParseOptions.map(({ label, value }: { label: string; value: IFCParseOption }) => (
                <Tab key={value} label={label} value={value} data-cy={`tab-ifc-parse-${value}`} />
              ))}
            </TabList>
          </Box>
          <Box sx={{ pt: 1 }}>{parseModeForm}</Box>
        </TabContext>
      </CorneredSection>

      <CorneredSection title="IFC-Eigenschaften" collapsible>
        <Box pt={2}>
          <IfcPropertiesView guids={guids} metadata={metadata} onSelectGuids={onSelectGuids} />
        </Box>
      </CorneredSection>

      {!ifcFileId && (
        <Typography variant="caption" color="text.secondary">
          Keine IFC-Datei ausgewählt. Bitte zuerst eine IFC-Datei im Projekt wählen.
        </Typography>
      )}
    </Stack>
  )
}

export default IFCElementForm
